import { Animated, Platform, StyleSheet, View, ViewProps } from 'react-native';
import { Layout, Text } from '@ui-kitten/components';
import { UIHelper as uh } from '../../../core';
import React, { useCallback, useEffect, useRef } from 'react';
import ModalHeader from '../../profileMeMarker/ModalHeader';
import moment from 'moment';
import { ScrollView } from 'react-native-gesture-handler';

//props
interface IHealthRecordDetailProps extends ViewProps {
  type: string;
  btnBackHandler: () => void;
  data: any;
}

const ANIMATE_DURATION = 500; //ms

//styles
const styleContainer = StyleSheet.create({
  screenContainer: {
    flex: 1,
    padding: uh.h2DP(16),
    paddingTop: uh.h2DP(16)
  },
  scrollView: {
    flex: 1
  },
  scrollContentContainer: {
    paddingBottom: uh.h2DP(24)
  },
  content: {
    marginTop: 16,
    borderRadius: 8,
    paddingTop: 16,
    paddingBottom: 16
  },
  animatedViewContainer: {
    height: uh.height() - (Platform.OS === 'ios' ? uh.topPos() : 0),
    position: 'absolute',
    bottom: Platform.OS === 'android' ? 0 : undefined,
    alignSelf: 'center',
    width: '100%'
  },
  nameContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginRight: uh.h2DP(16)
  },
  name: { marginLeft: uh.h2DP(16), marginRight: uh.h2DP(46) },
  score: { marginTop: uh.h2DP(10), marginLeft: uh.h2DP(16), marginRight: uh.h2DP(16) },
  divider: { marginTop: uh.h2DP(16) },
  summary: { marginTop: uh.h2DP(13), marginLeft: uh.h2DP(32) },
  pillars: {
    flex: 1,
    marginTop: uh.h2DP(16),
    marginLeft: uh.h2DP(16),
    marginRight: uh.h2DP(16),
    marginBottom: uh.h2DP(6)
  },
  goals: { marginLeft: uh.h2DP(32), marginTop: uh.h2DP(16) },
  individualAssessmentList: {
    marginTop: uh.h2DP(16),
    marginLeft: uh.h2DP(16),
    marginRight: uh.h2DP(16)
  },
  barchart: {
    marginTop: uh.h2DP(16),
    marginLeft: uh.w2DP(16),
    marginRight: uh.w2DP(16)
  },
  scoreChangeText: { marginTop: uh.h2DP(16), marginLeft: uh.h2DP(16), marginRight: uh.h2DP(16) },
  scoreChange: { marginTop: uh.h2DP(8), marginLeft: uh.h2DP(16), marginRight: uh.h2DP(16) },
  scoreChart: { marginTop: uh.h2DP(24), marginLeft: uh.h2DP(16), marginRight: uh.h2DP(16) },
  learnMore: { marginTop: uh.h2DP(16), marginLeft: uh.h2DP(16), marginRight: uh.h2DP(16) }
});

const HealthRecordDetailModal = ({ type, btnBackHandler, data }: IHealthRecordDetailProps) => {
  const moveAnim = useRef(new Animated.Value(uh.height())).current;

  const hasValue = (value: any) => {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return true;
  };

  const renderDateValue = (value?: string | Date) => {
    if (!value) {
      return 'N/A';
    }

    const date = moment(value);
    return date.isValid() ? date.format('DD MMM YYYY') : 'N/A';
  };

  const renderField = (label: string, value: any) => {
    if (!hasValue(value)) {
      return null;
    }

    return (
      <Text category="c2" style={{ fontWeight: 'bold' }}>
        {label}:
        <Text category="c1" appearance="hint">
          {' '}
          {String(value)}
        </Text>
      </Text>
    );
  };

  const renderListSection = (title: string, items?: any[], formatter?: (item: any) => string) => {
    if (!items?.length) {
      return null;
    }

    return (
      <>
        <Text category="s2" style={{ marginTop: 16 }}>
          {title}
        </Text>
        {items.map((item, idx: number) => (
          <Text category="c1" key={idx}>
            • {formatter ? formatter(item) : String(item)}
          </Text>
        ))}
      </>
    );
  };

  const moveUp = useCallback(() => {
    // Will decrease value to 30 in 500 milliseconds
    Animated.timing(moveAnim, {
      toValue: uh.topPos(),
      duration: ANIMATE_DURATION,
      useNativeDriver: true
    }).start();
  }, [moveAnim]);

  useEffect(() => {
    moveUp();
  }, [moveUp]);

  const moveDown = (callBackFnc: () => void) => {
    // Will increase value to windowHeight in 500 milliseconds
    Animated.timing(moveAnim, {
      toValue: uh.height(),
      duration: ANIMATE_DURATION,
      useNativeDriver: true
    }).start(() => {
      callBackFnc();
    });
  };

  const renderMedicationData = () => {
    const medication = data;
    return (
      <View>
        <Text category="s2">{medication.name}</Text>

        <Text category="c2" appearance="hint" style={{ marginVertical: 4 }}>
          Status: <Text category="c1"> {medication.status}</Text>
        </Text>

        {renderField('Intent', medication.intent)}
        {renderField('Category', medication.category)}
        {hasValue(medication.recordedDate) && renderField('Recorded Date', renderDateValue(medication.recordedDate))}
        {hasValue(medication.startDate) && renderField('Start Date', renderDateValue(medication.startDate))}
        {hasValue(medication.endDate) && renderField('End Date', renderDateValue(medication.endDate))}
        {hasValue(medication.authoredDate) && renderField('Authored Date', renderDateValue(medication.authoredDate))}
        {hasValue(medication.requester) && renderField('Requester', medication.requester)}
        {hasValue(medication.recorder) && renderField('Recorder', medication.recorder)}
        {hasValue(medication.courseOfTherapy) && renderField('Course Of Therapy', medication.courseOfTherapy)}
        {renderField('Reported', medication.reported ? 'Yes' : 'No')}
        {hasValue(medication.priorPrescriptionId) &&
          renderField('Prior Prescription ID', medication.priorPrescriptionId)}

        {renderListSection('RxNorm Codes', medication.rxNormCodes)}

        {medication.dosage && (
          <>
            <Text category="s2" style={{ marginTop: 16 }}>
              Dosage
            </Text>
            {renderField('Route', medication.dosage.route)}
            {hasValue(medication.dosage.quantity?.value) &&
              renderField(
                'Quantity',
                `${medication.dosage.quantity?.value}${hasValue(medication.dosage.quantity?.unit) ? ` ${medication.dosage.quantity.unit}` : ''}`
              )}
            {renderField('Instructions', medication.dosage.instructions)}
          </>
        )}

        {medication.dispenseQuantity && (
          <>
            <Text category="s2" style={{ marginTop: 16 }}>
              Dispense
            </Text>
            {hasValue(medication.dispenseQuantity.value) &&
              renderField(
                'Quantity',
                `${medication.dispenseQuantity.value}${
                  hasValue(medication.dispenseQuantity.unit) ? ` ${medication.dispenseQuantity.unit}` : ''
                }`
              )}
            {hasValue(medication.refillsAllowed) && renderField('Refills Allowed', medication.refillsAllowed)}
          </>
        )}

        {medication.reason && (
          <>
            <Text category="s2" style={{ marginTop: 16 }}>
              Reason
            </Text>
            {renderField('Name', medication.reason.name)}
            {renderListSection('Reason ICD-10 Codes', medication.reason.icd10Codes)}
          </>
        )}

        {renderListSection('Notes', medication.notes)}
      </View>
    );
  };

  const renderConditionData = () => {
    const condition = data;
    return (
      <View>
        <Text category="s2">{condition.name}</Text>

        <Text category="c2" appearance="hint" style={{ marginVertical: 4 }}>
          Status:
          <Text category="c1"> {condition.status}</Text>
        </Text>

        {renderField('Category', condition.category)}
        {renderField('Verification Status', condition.verificationStatus)}
        {hasValue(condition.onsetDate) && renderField('Onset Date', renderDateValue(condition.onsetDate))}
        {hasValue(condition.recordedDate) && renderField('Recorded Date', renderDateValue(condition.recordedDate))}

        {renderListSection('ICD-10 Codes', condition.icd10Codes)}
        {renderListSection('Evidence', condition.evidence)}
        {renderListSection('Notes', condition.notes)}
      </View>
    );
  };

  const renderAllergyData = () => {
    const allergy = data;
    return (
      <View>
        <Text category="s2">{allergy.name}</Text>

        <Text category="c2" appearance="hint" style={{ marginVertical: 4 }}>
          Status:
          <Text category="c1"> {allergy.status}</Text>
        </Text>

        {renderField('Criticality', allergy.criticality)}
        {renderField('Verification Status', allergy.verificationStatus)}
        {hasValue(allergy.onsetDate) && renderField('Onset Date', renderDateValue(allergy.onsetDate))}
        {hasValue(allergy.recordedDate) && renderField('Recorded Date', renderDateValue(allergy.recordedDate))}

        {renderListSection('SNOMED Codes', allergy.snomedCodes)}
        {renderListSection('RxNorm Codes', allergy.rxNormCodes)}
        {renderListSection('Categories', allergy.categories)}
        {renderListSection('Reactions', allergy.reactions)}
        {renderListSection('Notes', allergy.notes)}
      </View>
    );
  };

  const renderProcedureData = () => {
    const procedure = data;
    return (
      <View>
        <Text category="s2">{procedure.name}</Text>

        <Text category="c2" appearance="hint" style={{ marginVertical: 4 }}>
          Status:
          <Text category="c1"> {procedure.status}</Text>
        </Text>

        {renderField('Category', procedure.category)}
        {hasValue(procedure.recordedDate) && renderField('Recorded Date', renderDateValue(procedure.recordedDate))}
        {hasValue(procedure.performedDate) && renderField('Performed Date', renderDateValue(procedure.performedDate))}
        {renderField('Location', procedure.location)}
        {renderField('Body Site', procedure.bodySite)}
        {renderField('Recorder', procedure.recorder)}

        {renderListSection('CPT Codes', procedure.cptCodes)}
        {renderListSection('Performers', procedure.performers)}
        {renderListSection('Used Medications', procedure.usedMedications)}

        {procedure.reason && (
          <>
            <Text category="s2" style={{ marginTop: 16 }}>
              Reason
            </Text>
            {renderField('Name', procedure.reason.name)}
            {renderListSection('Reason SNOMED Codes', procedure.reason.snomedCodes)}
          </>
        )}

        {renderListSection('Focal Devices', procedure.focalDevices, (device: any) => {
          if (!device) {
            return '';
          }

          const details = [device.name, device.action ? `(${device.action})` : ''].filter(Boolean).join(' ');
          return details || JSON.stringify(device);
        })}
        {renderListSection('Notes', procedure.notes)}
      </View>
    );
  };

  const renderContent = () => {
    if (type == 'medications') {
      return renderMedicationData();
    } else if (type == 'procedures') {
      return renderProcedureData();
    } else if (type == 'conditions') {
      return renderConditionData();
    } else if (type == 'allergies') {
      return renderAllergyData();
    }
  };

  return (
    <Animated.View
      style={{
        ...styleContainer.animatedViewContainer,
        transform: [
          {
            translateY: moveAnim
          }
        ]
      }}>
      <ModalHeader
        caption="Details"
        btnClickHandler={() => {
          moveDown(btnBackHandler);
        }}
      />

      <Layout level="2" style={styleContainer.screenContainer}>
        <ScrollView
          style={styleContainer.scrollView}
          contentContainerStyle={styleContainer.scrollContentContainer}
          showsVerticalScrollIndicator={false}>
          {renderContent()}
        </ScrollView>
      </Layout>
    </Animated.View>
  );
};

export default HealthRecordDetailModal;
